import { AlertDescription, Typography, Separator, Loading, Button, Alert } from '@ui/components';
import { WarningFilled, Users, Car } from '@ui/components/icons';
import { RemoveImportModal } from '@pages/onboarding/containers';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { useOnboardingImportSummary } from './use-onboarding-import-summary';

export const OnboardingImportSummary = () => {
  const {
    isGetErrorsLoading,
    importSummaryData,
    isQueryLoading,
    isSaveLoading,
    onGetErrors,
    onSave,
  } = useOnboardingImportSummary();
  const { t } = useTranslation();

  const [isRemoveImportModalOpen, setIsRemoveImportModalOpen] = useState(false);
  const numberOfErrors =
    importSummaryData?.employees.numOfErrors || importSummaryData?.vehicles.numOfErrors;

  if (isQueryLoading) {
    return <Loading />;
  }

  return (
    <section className="space-y-6">
      <Typography variant="h400" element="h1">
        {t('ONBOARDING.IMPORT_SUMMARY.TITLE')}
      </Typography>

      <Typography className="mt-8" variant="h200">
        {t('ONBOARDING.IMPORT_SUMMARY.DESCRIPTION')}
      </Typography>

      <section className="w-full p-4 rounded-lg border border-border-default space-y-6 mb-0">
        <div className="flex gap-2 items-center">
          <Car className="text-disabled size-5" />
          <Typography variant="h100">{t('ONBOARDING.IMPORT_SUMMARY.VEHICLES')}</Typography>
        </div>

        <div className="grid grid-cols-3 px-4">
          <Typography variant="paragraphBasicBook">
            {importSummaryData?.vehicles.numOfEntities}{' '}
            {t('ONBOARDING.IMPORT_SUMMARY.ELEMENTS_interval', {
              postProcess: 'interval',
              count: importSummaryData?.vehicles.numOfEntities,
            })}
          </Typography>

          <Typography variant="paragraphBasicBook">
            {importSummaryData?.vehicles.numOfErrors}{' '}
            {t('ONBOARDING.IMPORT_SUMMARY.ERRORS_interval', {
              postProcess: 'interval',
              count: importSummaryData?.vehicles.numOfErrors,
            })}
          </Typography>
        </div>

        <div className="flex gap-2 items-center">
          <Users className="text-disabled size-5" />
          <Typography variant="h100">{t('ONBOARDING.IMPORT_SUMMARY.EMPLOYEES')}</Typography>
        </div>

        <div className="grid grid-cols-3 px-4">
          <Typography variant="paragraphBasicBook">
            {importSummaryData?.employees.numOfEntities}{' '}
            {t('ONBOARDING.IMPORT_SUMMARY.ELEMENTS_interval', {
              postProcess: 'interval',
              count: importSummaryData?.employees.numOfEntities,
            })}
          </Typography>

          <Typography variant="paragraphBasicBook">
            {importSummaryData?.employees.numOfErrors}{' '}
            {t('ONBOARDING.IMPORT_SUMMARY.ERRORS_interval', {
              postProcess: 'interval',
              count: importSummaryData?.employees.numOfErrors,
            })}
          </Typography>
        </div>
      </section>

      <Button
        onClick={() => setIsRemoveImportModalOpen(true)}
        disabled={isGetErrorsLoading || isSaveLoading}
        className="block ml-auto"
        variant="link"
      >
        {t('ONBOARDING.IMPORT_SUMMARY.REMOVE_IMPORT_AND_RETRY')}
      </Button>

      <RemoveImportModal onOpenChange={setIsRemoveImportModalOpen} open={isRemoveImportModalOpen} />

      <Typography className="mt-8" variant="h200">
        {t('ONBOARDING.IMPORT_SUMMARY.MANAGE_ERRORS')}
      </Typography>

      {!!numberOfErrors && (
        <Button
          disabled={isGetErrorsLoading || isSaveLoading}
          isLoading={isGetErrorsLoading}
          onClick={() => onGetErrors()}
          variant="secondary"
          wide
        >
          {t('ONBOARDING.IMPORT_SUMMARY.DOWNLOAD_ERRORS_REPORT')}
        </Button>
      )}

      <Separator />

      {!importSummaryData?.employees.numOfEntities && (
        <Alert variant="critical">
          <AlertDescription className="flex gap-2 items-center">
            <WarningFilled className="inline-block" />
            {t('ONBOARDING.IMPORT_SUMMARY.NO_EMPLOYEES_ERROR')}
          </AlertDescription>
        </Alert>
      )}

      <Button
        disabled={
          isSaveLoading || isGetErrorsLoading || !importSummaryData?.employees.numOfEntities
        }
        isLoading={isSaveLoading}
        onClick={() => onSave()}
        variant="primary"
        size="large"
        wide
      >
        {t('ONBOARDING.IMPORT_SUMMARY.SAVE_AND_CONTINUE')}
      </Button>
    </section>
  );
};
